import type { Board } from "../board";
import { DamageType } from "../damageType";
import type { Direction } from "../direction";
import { ALL_DIRECTIONS, directionDelta, directionFromVector, cycleDirection } from "../direction";
import { Explosion } from "../effects/explosion";
import { MovementEffect } from "../effects/movementEffect";
import type { PointsToDirection } from "../interfaces";
import type { PhysicalThingWithHp } from "../physicalThingWithHp";
import { STD_BASE_HP } from "../physicalThingWithHp";
import { Projectile } from "../projectile";
import { randomChance } from "../random";
import { deserializeXY, serializeXY } from "../serialization";
import { XY } from "../xy";

function steerTowardsTarget(current: Direction, desired: Direction): Direction {
	if (current === desired) return current;

	const currentIdx = ALL_DIRECTIONS.indexOf(current);
	const desiredIdx = ALL_DIRECTIONS.indexOf(desired);
	const clockwiseDist = (desiredIdx - currentIdx + 8) % 8;

	if (clockwiseDist === 4) {
		// Exactly opposite — randomly choose direction
		return cycleDirection(current, randomChance(0.5) ? 1 : -1);
	}

	// Cycle toward the shorter arc
	return clockwiseDist <= 4 ? cycleDirection(current, 1) : cycleDirection(current, -1);
}

export class HomingMissile extends Projectile implements PointsToDirection {
	static key = "HomingMissile";
	direction: Direction;
	private fuel: number;
	private target: XY;

	constructor(xy: XY, direction: Direction, target: XY) {
		super(xy);
		this.direction = direction;
		this.fuel = 100;
		this.target = target;
	}

	processTurn(board: Board): void {
		super.processTurn(board);
		if (!this.active) return;
		if (board.currentProcessingPass !== 0) return;

		this.fuel--;
		if (this.fuel <= 0) {
			board.removeAtEndOfTurn(this);
			return;
		}

		// Steer toward target
		const desiredDirection = this.getDirectionToTarget();
		if (desiredDirection && desiredDirection !== this.direction) {
			this.direction = steerTowardsTarget(this.direction, desiredDirection);
		}

		const destination = this.xy.add(directionDelta(this.direction));

		// Explode on blocked cells
		if (!board.isInBounds(destination)) {
			board.removeAtEndOfTurn(this);
			return;
		}
		if (!board.isPassable(destination)) {
			board.addEffect(new Explosion(destination));
			this.onHit(0);
			return;
		}

		const startTurnXY = this.xy;
		this.moveTo(destination);
		board.addEffect(new MovementEffect(this, startTurnXY, this.xy));
	}

	private getDirectionToTarget(): Direction | null {
		const dx = this.target.x - this.xy.x;
		const dy = this.target.y - this.xy.y;
		if (dx === 0 && dy === 0) return null;
		return directionFromVector(new XY(Math.sign(dx), Math.sign(dy)));
	}

	protected damageTarget(target: PhysicalThingWithHp): number {
		return target.applyDamage(DamageType.EXPLOSION, STD_BASE_HP, this);
	}

	onHit(_damageDealt: number): void {
		if (!this.active) return;
		this.board!.addEffect(new Explosion(this.xy));
		this.board!.removeAtEndOfTurn(this);
	}

	getDirection(): Direction | null {
		return this.direction;
	}

	setDirection(dir: Direction): void {
		this.direction = dir;
	}

	serialize(): Record<string, unknown> {
		return {
			...super.serialize(),
			direction: this.direction,
			fuel: this.fuel,
			target: serializeXY(this.target),
		};
	}

	static deserialize(data: any): HomingMissile {
		const m = new HomingMissile(deserializeXY(data.xy), data.direction as Direction, deserializeXY(data.target));
		m.fuel = data.fuel;
		m.visualId = data.visualId;
		return m;
	}
}
